import {
  type AutomationsAnalyticsEntrySource,
  type AutomationsAnalyticsListStatus,
  type AutomationsAnalyticsPromptMode,
  type AutomationsAnalyticsRunStatus,
  type AutomationsAnalyticsScheduleRepeat,
  type UseTrackAutomationCreatedParams,
} from '@air/analytics';

import { getAutomationRunPromptMode } from '~/components/Automations/detail/automationRunPrompt';
import { resolveAutomationListStatus } from '~/components/Automations/utils/automationListDisplay';
import { parseAutomationSchedule } from '~/components/Automations/utils/automationSchedule';
import { type AutomationRun } from '~/hooks/queries/automations/usePrivateAutomationRunsList';

export const toAutomationsAnalyticsPromptMode = (prompt: string): AutomationsAnalyticsPromptMode | null => {
  const mode = getAutomationRunPromptMode(prompt);

  if (mode === 'unset') {
    return null;
  }

  return mode === 'skillLinked' ? 'skill_linked' : 'custom';
};

export const toAutomationsAnalyticsScheduleRepeat = ({
  cronSchedule,
  now,
  timeZone,
}: {
  cronSchedule: string;
  now: Date;
  timeZone: string;
}): AutomationsAnalyticsScheduleRepeat | undefined => {
  const parsed = parseAutomationSchedule({ cronSchedule, now, timeZone });

  return parsed?.repeat ?? undefined;
};

export const toAutomationsAnalyticsListStatus = (
  automation: Parameters<typeof resolveAutomationListStatus>[0],
): AutomationsAnalyticsListStatus => resolveAutomationListStatus(automation);

export const resolveAutomationsEntrySource = (hasPrefillQueryKey: boolean): AutomationsAnalyticsEntrySource =>
  hasPrefillQueryKey ? 'skill_automate' : 'in_app';

export type BuildAutomationCreatedAnalyticsParamsInput = {
  cronSchedule: string;
  hasPrefillQueryKey: boolean;
  now: Date;
  prompt: string;
  timeZone: string;
};

export const buildAutomationCreatedAnalyticsParams = ({
  cronSchedule,
  hasPrefillQueryKey,
  now,
  prompt,
  timeZone,
}: BuildAutomationCreatedAnalyticsParamsInput): UseTrackAutomationCreatedParams | null => {
  const promptMode = toAutomationsAnalyticsPromptMode(prompt);

  if (!promptMode) {
    return null;
  }

  const scheduleRepeat = toAutomationsAnalyticsScheduleRepeat({
    cronSchedule,
    now,
    timeZone,
  });

  return {
    entry_source: resolveAutomationsEntrySource(hasPrefillQueryKey),
    prompt_mode: promptMode,
    ...(scheduleRepeat ? { schedule_repeat: scheduleRepeat } : {}),
  };
};

export const toAutomationsAnalyticsRunStatus = (status: AutomationRun['status']): AutomationsAnalyticsRunStatus =>
  status;
